import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Tabs, Tab, Button, Avatar, Chip, CircularProgress, Paper } from '@mui/material'
import Person from '@mui/icons-material/Person'
import Save from '@mui/icons-material/Save'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { useKycApprovalForm } from './hooks/useKycApprovalForm'
import { SECTION_CONFIG, ALL_SECTIONS } from './utils/kycApprovalUtils'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

// Import section components (will create these next)
import KycSection from './components/sections/KycSection'
import FunctionsSection from './components/sections/FunctionsSection'
import AssignmentsSection from './components/sections/AssignmentsSection'
import PermissionsSection from './components/sections/PermissionsSection'
import PayoutSection from './components/sections/PayoutSection'
import OpsSection from './components/sections/OpsSection'

/**
 * Main KYC approval form with 6 tabs
 * Supports both new and edit modes via props
 */
export default function KycApprovalForm({ visibleSections = ALL_SECTIONS, mode = 'approve' }) {
  const { requestId, therapistId } = useParams()
  const navigate = useNavigate()

  const {
    formData,
    therapist,
    activeTab,
    isDirty,
    loading,
    errors,
    saving,
    setActiveTab,
    updateSection,
    saveAndApprove,
  } = useKycApprovalForm(requestId, therapistId, mode)

  const isDisabled = mode === 'update'

  // Filter visible sections
  const visibleSectionConfigs = SECTION_CONFIG.filter((section) =>
    visibleSections.includes(section.key),
  )

  // Section component mapping
  const sectionComponents = {
    kyc: KycSection,
    functions: FunctionsSection,
    assignments: AssignmentsSection,
    permissions: PermissionsSection,
    payout: PayoutSection,
    ops: OpsSection,
  }

  const handleSaveAndApprove = async () => {
    const success = await saveAndApprove(visibleSections)
    if (success) {
      navigate('/kyc-approvals')
    }
  }

  if (loading.request) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <SupportHeader heading="KYC Approval Form" />

      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
        {/* Therapist Info Card */}
        {therapist && (
          <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={therapist.profilePicId ? getImageLink(therapist.profilePicId) : null}
              sx={{ width: 60, height: 60 }}
            >
              <Person />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{therapist.name}</div>
              {therapist.phone && (
                <div style={{ fontSize: '0.9rem', color: '#666' }}>{therapist.phone}</div>
              )}
              {therapist.slug && (
                <Chip label={therapist.slug} size="small" sx={{ mt: 0.5 }} />
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              disabled={!isDirty || saving || isDisabled}
              onClick={handleSaveAndApprove}
            >
              {saving ? 'Saving...' : 'Save & Approve'}
            </Button>
          </Paper>
        )}

        {/* Section Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable">
            {visibleSectionConfigs.map((section) => (
              <Tab key={section.key} label={section.label} />
            ))}
          </Tabs>
        </Paper>

        {/* Section Content */}
        <Paper sx={{ p: 3 }}>
          {visibleSectionConfigs.map((section, index) => {
            if (activeTab !== index) return null

            const SectionComponent = sectionComponents[section.key]
            if (!SectionComponent) {
              return <div key={section.key}>Section component not found: {section.key}</div>
            }

            return (
              <SectionComponent
                key={section.key}
                data={formData[section.key]}
                onChange={(data) => updateSection(section.key, data)}
                disabled={isDisabled}
                loading={loading[section.key]}
                error={errors[section.key]}
              />
            )
          })}
        </Paper>

        {/* Bottom Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/kyc-approvals')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={!isDirty || saving || isDisabled}
            onClick={handleSaveAndApprove}
          >
            {saving ? 'Saving...' : 'Save & Approve'}
          </Button>
        </Box>
      </Box>
    </>
  )
}
